/**
 * Optional CloudWatch sink for @workspace/newrelic-browser.
 *
 * When configured, every `addPageAction` call is POSTed to a server-side
 * endpoint in addition to being forwarded to the NR browser agent. The
 * endpoint (a Next.js API route) logs structured JSON to stdout, which
 * Lambda captures and ships to CloudWatch automatically.
 *
 * The sink is off by default. Activate it by mounting `NewRelicCloudWatchSink`
 * in providers.tsx (which calls `configureCloudWatchSink` once).
 *
 * Design constraints:
 * - Fire-and-forget: never awaited, never throws, never blocks the caller.
 * - `keepalive: true` so in-flight requests survive a navigation.
 * - Optional `filterEvents` allowlist to avoid CloudWatch noise from
 *   high-frequency events (e.g. page-view actions on every route change).
 */

export interface CloudWatchSinkConfig {
  /**
   * The API route endpoint to POST events to.
   * Typically '/api/log-event'. Use `createLogEventHandler` to create the route.
   */
  endpoint: string;
  /**
   * Optional allowlist of event names to forward to the sink.
   * When omitted, all addPageAction events are forwarded.
   * Recommended: pass only your key named events to limit log volume.
   * @example ['form_step_submitted', 'broken_journey_resumed']
   */
  filterEvents?: ReadonlyArray<string>;
}

let _config: CloudWatchSinkConfig | null = null;

/**
 * Activate the CloudWatch sink. Idempotent — safe to call more than once
 * (later calls overwrite the previous config). Typically called once from a
 * client component (see NewRelicCloudWatchSink) at app startup.
 */
export function configureCloudWatchSink(config: CloudWatchSinkConfig): void {
  _config = config;
}

/**
 * Called by addPageAction when the sink is active. Fire-and-forget.
 * Internal — not part of the public package API.
 */
export function postToCloudWatch(
  name: string,
  attributes?: Record<string, unknown>,
): void {
  if (_config === null) return;
  if (_config.filterEvents && !_config.filterEvents.includes(name)) return;

  try {
    void fetch(_config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, attributes }),
      // keepalive: the request survives a page navigation so events fired
      // immediately before a route change are not dropped.
      keepalive: true,
    }).catch(() => {
      // Swallow network errors — logging is best-effort.
    });
  } catch {
    // Swallow synchronous errors (e.g. serialisation failure).
  }
}

/** Reset module-level state for testing only. */
export function _resetCloudWatchSinkForTesting(): void {
  _config = null;
}
